/**
 * Parent status roll-up prompt (card UD7blPRrNa0C).
 *
 * Client feedback 2026-09-30: people mark sub-activities complete one by one,
 * and after the LAST one the parent still does not show Completed. Nothing in
 * the app rolls children up to the parent (the server cascade endpoint only
 * back-fills EARLIER same-area stages by autocomplete_order; it never touches
 * parent_activity_id). So after a child status change we ASK — never change the
 * parent silently:
 *
 *   "All N sub-activities of <parent> are now <status>. Set <parent> to <status> too?"
 *
 * - getParentRollupStatusId (client/utils/parentStatusRollup.ts) is the rule.
 * - findParentRollup reads the parent + its children fresh from the server, so
 *   it sees the state AFTER the change (and after any auto-complete back-fill).
 * - ParentStatusRollupProvider is mounted once in the web layout, so the prompt
 *   survives the row / batch bar that triggered it unmounting (e.g. a completed
 *   row hiding on /activities, or the batch bar closing when selection clears).
 *   "Set parent" writes through useActivityStatusUpdate — the SAME write the
 *   status badge, sidebars and batch bars use for any activity, so
 *   completed_date / completed_by / square_footage_claimed / was_autocompleted
 *   match a manual change.
 * - ParentStatusRollupDialog is the presentational dialog, also used by the
 *   field activity page (which writes through its own offline-aware path).
 */

import { Layers, Loader2 } from 'lucide-react';
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

import { notifications } from '../../../config/toast-notifications.js';
import { activitiesExtensions } from '../../api/extensions/activities-business-logic-extension.js';
import type { Activities } from '../../api/generated_schemas/generated-activities-schema.js';
import { useActivityStatusUpdate } from '../../api/non_generated_business_hooks/activities_business_hooks/useActivityStatusUpdate.js';
import { getParentRollupStatusId } from '../../utils/parentStatusRollup.js';
import { BoulderDialog } from '../ui/boulder-dialog.js';
import { Button } from '../ui/button.js';

export interface ParentRollupCandidate {
  parentId: string;
  parent: Activities;
  childCount: number;
  /** The status every child now shares (differs from the parent's). */
  statusId: string;
}

/**
 * Read the parent + its children from the server and apply the roll-up rule.
 * isTerminal = activity_statuses.is_terminal lookup (never reopen a terminal parent).
 */
export async function findParentRollup(
  parentId: string,
  isTerminal: (statusId: string) => boolean,
): Promise<ParentRollupCandidate | null> {
  const [parent, children] = await Promise.all([
    activitiesExtensions.getById(parentId),
    activitiesExtensions.getAll({ filters: { parent_activity_id: parentId } }),
  ]);
  if (!parent) {return null;}
  const statusId = getParentRollupStatusId(parent.status_id, children.map((c) => c.status_id), isTerminal);
  return statusId ? { parentId, parent, childCount: children.length, statusId } : null;
}

export function parentRollupName(parent: Pick<Activities, 'name' | 'trig_activity_type_name'>): string {
  return parent.name || parent.trig_activity_type_name || 'the parent activity';
}

interface ParentStatusRollupDialogProps {
  open: boolean;
  parentName: string;
  childCount: number;
  statusName: string;
  isProcessing: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ParentStatusRollupDialog({
  open,
  parentName,
  childCount,
  statusName,
  isProcessing,
  onConfirm,
  onCancel,
}: ParentStatusRollupDialogProps) {
  return (
    <BoulderDialog
      open={open}
      onOpenChange={(o) => { if (!o && !isProcessing) { onCancel(); } }}
      tone="indigo"
      width="md"
      icon={<Layers className="h-5 w-5" />}
      title="Update the parent activity?"
      description={
        <>
          All {childCount} sub-{childCount === 1 ? 'activity' : 'activities'} of{' '}
          <span className="font-semibold text-foreground">{parentName}</span>{' '}
          {childCount === 1 ? 'is' : 'are'} now{' '}
          <span className="font-semibold text-foreground">{statusName}</span>.
          {' '}Set <span className="font-semibold text-foreground">{parentName}</span> to{' '}
          <span className="font-semibold text-foreground">{statusName}</span> too?
        </>
      }
    >
      <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={isProcessing}>
          Not now
        </Button>
        <Button onClick={onConfirm} disabled={isProcessing}>
          {isProcessing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Set parent to {statusName}
        </Button>
      </div>
    </BoulderDialog>
  );
}

type PromptForParents = (parentIds: ReadonlyArray<string | null | undefined>) => void;

// Default is a no-op, so a component rendered outside the provider (tests,
// storybook-style mounts) simply never prompts.
const ParentStatusRollupContext = createContext<PromptForParents>(() => {});

/**
 * Returns promptForParents(parentIds): call it AFTER a successful child status
 * change with the changed activities' parent_activity_id values. Each parent
 * whose children now all share one status different from its own is queued and
 * asked about one at a time.
 */
export function useParentStatusRollup(): PromptForParents {
  return useContext(ParentStatusRollupContext);
}

export function isActivityAssigned(activity: Pick<Activities, 'trig_assigned_users'>): boolean {
  const assigned = activity.trig_assigned_users as unknown;
  return Array.isArray(assigned) && assigned.length > 0;
}

export function ParentStatusRollupProvider({ children }: { children: ReactNode }) {
  const { availableStatuses, updateActivityStatus } = useActivityStatusUpdate();
  const [queue, setQueue] = useState<ParentRollupCandidate[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  // Latest statuses for the terminal rule, read inside the stable callback.
  const statusesRef = useRef(availableStatuses);
  statusesRef.current = availableStatuses;

  const promptForParents = useCallback<PromptForParents>((parentIds) => {
    const statuses = statusesRef.current;
    // Without the status list we can't apply the "never reopen" rule: skip.
    if (!statuses || statuses.length === 0) {return;}
    const isTerminal = (statusId: string) => statuses.find((s) => s.id === statusId)?.is_terminal === true;
    const ids = Array.from(new Set(parentIds.filter((id): id is string => !!id)));
    for (const id of ids) {
      findParentRollup(id, isTerminal)
        .then((found) => {
          if (!found) {return;}
          setQueue((q) => (q.some((c) => c.parentId === found.parentId) ? q : [...q, found]));
        })
        // Read-only check: a failure just means no prompt.
        .catch((err) => console.error('Parent status roll-up check failed:', err));
    }
  }, []);

  const current = queue[0];
  const status = current ? availableStatuses?.find((s) => s.id === current.statusId) : undefined;
  const next = () => setQueue((q) => q.slice(1));

  const handleConfirm = async () => {
    if (!current || !status) {return;}
    setIsProcessing(true);
    try {
      await updateActivityStatus({
        activityId: current.parentId,
        statusId: current.statusId,
        isAssigned: isActivityAssigned(current.parent),
        lotId: current.parent.trig_area_lot_id ?? null,
      });
      notifications.success('Success', `Activity status updated to "${status.name}"`);
    } catch (err) {
      // useActivityStatusUpdate already rolled back + toasted the error.
      console.error('Failed to update parent activity status:', err);
    } finally {
      setIsProcessing(false);
      next();
    }
  };

  return (
    <ParentStatusRollupContext.Provider value={promptForParents}>
      {children}
      {current && status && (
        <ParentStatusRollupDialog
          key={current.parentId}
          open
          parentName={parentRollupName(current.parent)}
          childCount={current.childCount}
          statusName={status.name}
          isProcessing={isProcessing}
          onConfirm={handleConfirm}
          onCancel={next}
        />
      )}
    </ParentStatusRollupContext.Provider>
  );
}
